// The week: shape it around life, then commit. A ring for the ask, seven days you can open,
// a tick to choose, one button to book everything chosen. Opening a day brings up a sheet
// with that day's band to drag, the routes that fit, and Book, Move to another day, Cancel.
import { useEffect, useRef, useState } from "preact/hooks";
import { homeOf } from "../data/empty";
import { haptic } from "../lib/haptic";
import { useMusette, bookRides, buildWeekFromDiary, toggleChosen, cancelRide, restoreRide, moveRide, addRide, pullRefresh, setupStatus } from "../lib/store";
import { Pull } from "../components/Pull";
import { missedSince } from "../lib/coach";
import { wallsFor, clampStart, validStarts, type Wall } from "../lib/day";
import { hhmm } from "../lib/sun";
import { AXIS_START, AXIS_MIN } from "../lib/band";
import { dayShort, dayNum, shortDate, addDays, mondayOf, todayIso, dayLong, dateLabel, weeksTo } from "../lib/dates";
import { sunTimes, toMin } from "../lib/sun";
import { hourlyDay, type HourlyDay } from "../lib/weather";
import { Band } from "../components/Band";
import { Head } from "../components/Head";
import { RideEditor } from "../components/RideEditor";
import { Sheet, Ring, useToast } from "../components/Sheet";
import { rampWeekNo } from "./Today";
import type { Day, Kept } from "../lib/band";
import type { PlanDay, State } from "../lib/types";

const WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];

export function Week() {
  const s = useMusette();
  const today = todayIso();
  const q = typeof location !== "undefined" ? new URLSearchParams(location.search) : new URLSearchParams();
  const thisMonday = mondayOf(today);
  const [which, setWhich] = useState<0 | 1>(q.get("w") === "next" || (q.get("d") && q.get("d")! > addDays(thisMonday, 6)) ? 1 : 0);
  const [open, setOpen] = useState<string | null>(q.get("d"));
  const target = addDays(thisMonday, 7 * which);
  const week = s.plan.find((p) => p.monday === target);
  const ramp = s.ramp.find((r) => r.w === target);
  const chosen = week?.days.filter((d) => d.status === "ticked") ?? [];
  const home = homeOf(s);
  const { show, node: toast } = useToast();
  const [busy, setBusy] = useState(false);
  const n = rampWeekNo(s.ramp, target);
  const i0 = Math.max(0, s.ramp.findIndex((r) => r.w === target));
  const six = s.ramp.slice(i0, i0 + 6);
  const [rampPick, setRampPick] = useState(0);
  const maxKm = Math.max(1, ...six.map((r) => r.km ?? 0));

  // When the diary was last read for clashes (every half hour by the timer), not when the week was first built.
  const [diaryAt, setDiaryAt] = useState<string | null>(null);
  useEffect(() => { setupStatus().then((st) => setDiaryAt(st?.tick?.diaryAt ?? null)); }, []);
  const ago = (iso: string) => { const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000)); return m < 2 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : shortDate(iso); };
  // A ride this week that ended with nothing on Strava overtakes the ramp's note for it (checklist 14).
  const overtaken = target === mondayOf(today) && !!missedSince(s, today, target);
  const [hours, setHours] = useState<Record<string, HourlyDay | null>>({});
  useEffect(() => {
    let live = true;
    (async () => { const h: Record<string, HourlyDay | null> = {}; for (let i = 0; i < 7; i++) { const d = addDays(target, i); h[d] = await hourlyDay(home.lat, home.lon, d); } if (live) setHours(h); })();
    return () => { live = false; };
  }, [target]);

  const rides = week?.days.filter((d) => d.type === "ride" && d.status !== "cancelled") ?? [];
  const doneCount = rides.filter((d) => d.status === "done" || s.rides.some((r) => r.d === d.date && r.km >= 15)).length;
  const kmDone = Math.round(s.rides.filter((r) => r.d >= target && r.d <= addDays(target, 6)).reduce((a, r) => a + r.km, 0));
  const ask = ramp?.rides ? `${WORDS[ramp.rides] ?? ramp.rides} ${ramp.rides === 1 ? "ride" : "rides"}` : ramp?.km ? `${ramp.km} km` : "No target";
  const build = async () => { setBusy(true); show(await buildWeekFromDiary(target)); setBusy(false); };
  const openDay = week?.days.find((d) => d.date === open);
  const swipe = useRef<number | null>(null);

  return (
    <main class="screen signal">
      <Head right={`${shortDate(target)} to ${shortDate(addDays(target, 6))}`} />

      <div class="stack" style="margin-top: var(--space-3)">
        <div class="segmented text" role="tablist">
          <button type="button" role="tab" aria-selected={which === 0} onClick={() => setWhich(0)}>This week</button>
          <button type="button" role="tab" aria-selected={which === 1} onClick={() => setWhich(1)}>Next week</button>
        </div>
      </div>

      <Pull onRefresh={async () => { const m = await pullRefresh(); if (week) await buildWeekFromDiary(target).catch(() => null); return m; }} />
      <section class="stack week-head" onTouchStart={(e) => { swipe.current = e.touches[0].clientX; }} onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (swipe.current ?? e.changedTouches[0].clientX); if (dx < -60 && which === 0) setWhich(1); if (dx > 60 && which === 1) setWhich(0); swipe.current = null; }}>
        <Ring done={ramp?.km && !ramp.rides ? kmDone : doneCount} of={ramp?.km && !ramp.rides ? ramp.km : (ramp?.rides ?? Math.max(1, rides.length))} segments={!(ramp?.km && !ramp.rides)}>
          <span class="stat-n">{ramp?.km && !ramp.rides ? kmDone : doneCount}</span>
          <span class="label">of {ramp?.km && !ramp.rides ? ramp.km : ramp?.rides ?? rides.length}</span>
        </Ring>
        <div class="week-ask">
          <span class="label amber">{n !== "–" ? `Week ${String(n).padStart(2, "0")} of ${s.ramp.length}` : "Before the ramp"}</span>
          <span class="ask">{ask}</span>
          <span class="soft">{!week ? "Not built yet." : overtaken ? rideDays(week.days) : ramp?.note && !/^First week back/.test(ramp.note) ? `${ramp.note}.` : rideDays(week.days)}</span>
        </div>
      </section>

      <section class="stack" style="margin-top: var(--space-5)">
        {!week && (
          <div class="card">
            <span class="soft">Musette reads your diary, the plan and the light, then proposes the rides. Nothing goes in the diary until you book.</span>
            <button type="button" class="pill" onClick={build}>{busy ? "Reading your diary" : "Build the week from your diary"}</button>
          </div>
        )}

        {week && (
          <div class="card days">
            {week.days.map((d) => <DayRow d={d} s={s} today={today} hours={hours[d.date] ?? null} open={() => setOpen(d.date)} toast={show} />)}
          </div>
        )}

        {six.length > 0 && (
          <div class="card">
            <div class="row-between"><span class="label ink">The plan</span><span class="label">Paris in {s.stages[0] ? weeksTo(today, s.stages[0].date) : "–"} weeks</span></div>
            <div class="ramp" role="radiogroup" aria-label="The next six weeks">
              {six.map((r, k) => {
                const h = r.km ? Math.max(14, Math.round((r.km / maxKm) * 100)) : 14 + (r.rides ?? 0) * 8;
                return <button type="button" role="radio" aria-checked={k === rampPick} class={`${k === 0 ? "now" : ""}${k === rampPick ? " picked" : ""}`} style={`height:${h}%`} onClick={() => setRampPick(k)} aria-label={`Week of ${shortDate(r.w)}`} />;
              })}
            </div>
            <div class="ramp-labels">{six.map((r, k) => <span class={`label${k === 0 ? " amber" : ""}`}>{r.rides ? `${r.rides} ${r.rides === 1 ? "ride" : "rides"}` : r.km ? `${r.km} km` : "–"}</span>)}</div>
            <span class="soft">{rampLine(rampPick === 0 && overtaken ? { ...six[0], note: undefined } : six[rampPick], rampPick)}</span>
          </div>
        )}

        {week && (
          <div class="row-between">
            <span class="label">{diaryAt ? `Diary checked ${ago(diaryAt)}` : `Diary read${week.readAt ? ` ${shortDate(week.readAt)}` : ""}`}</span>
            <span class="label">Pull down to read it again</span>
          </div>
        )}
      </section>

      <div class="dock-wrap">
        {toast}
        {chosen.length > 0 && (
          <div class="dock">
            <button type="button" class="pill wide" disabled={busy} onClick={async () => { setBusy(true); show(await bookRides(chosen.map((d) => d.date))); setBusy(false); }}>
              {busy ? "Putting them in the diary" : `Book ${chosen.length === 1 ? dayLong(chosen[0].date) : `${chosen.length} rides`}`}
            </button>
          </div>
        )}
      </div>

      <Sheet open={!!openDay} onClose={() => { setOpen(null); history.replaceState(null, "", "/week"); }} label={openDay ? dayLong(openDay.date) : "Day"}>
        {openDay && <DaySheet d={openDay} s={s} today={today} week={week!.days} toast={show} close={() => setOpen(null)} />}
      </Sheet>
    </main>
  );
}

/**
 * A day in the week. Tap to open it. Swipe left to call a ride off, right to choose it (each with
 * Undo). Press and hold the band, then drag, to move a proposed ride's time; it stops at the walls.
 */
function DayRow({ d, s, today, hours, open, toast }: { d: PlanDay; s: State; today: string; hours: HourlyDay | null; open: () => void; toast: (t: string, u?: () => void) => void }) {
  const home = homeOf(s);
  const sun = sunTimes(home.lat, home.lon, d.date);
  const isRide = d.type === "ride" && d.status !== "cancelled" && d.start && d.end;
  const done = d.status === "done" || s.rides.some((r) => r.d === d.date && r.km >= 15);
  const past = d.date < today;
  const [dx, setDx] = useState(0);
  const [drag, setDrag] = useState<number | null>(null);
  const g = useRef<{ x: number; y: number; t: number; mode: "none" | "swipe" | "drag" | "scroll"; hold?: number; w?: number } | null>(null);
  const bandRef = useRef<HTMLSpanElement>(null);
  const movable = isRide && !done && !past && d.status !== "booked";
  const dur = isRide ? toMin(d.end!) - toMin(d.start!) : 0;
  const walls: Wall[] = isRide ? wallsFor(s, d.date, keptWindows([d]).map((k) => ({ start: k.start, end: k.end, label: k.label ?? "Kept" }))) : [];
  const start = drag ?? (isRide ? toMin(d.start!) : 0);
  const day: Day = { date: d.date, rise: sun.rise, set: sun.set, hourly: hours, rides: isRide ? [{ start, end: start + dur, done }] : [], kept: keptWindows([d]), rest: d.type === "rest" };

  const down = (e: PointerEvent) => {
    g.current = { x: e.clientX, y: e.clientY, t: Date.now(), mode: "none", w: bandRef.current?.getBoundingClientRect().width };
    if (movable && (e.target as Element).closest(".band-wrap")) g.current.hold = window.setTimeout(() => { if (g.current && g.current.mode === "none") { g.current.mode = "drag"; setDrag(toMin(d.start!)); } }, 380);
  };
  const move = (e: PointerEvent) => {
    const st = g.current; if (!st) return;
    const mx = e.clientX - st.x, my = e.clientY - st.y;
    if (st.mode === "none") {
      if (Math.abs(my) > 10) { st.mode = "scroll"; clearTimeout(st.hold); return; }
      if (Math.abs(mx) > 12 && !past && isRide && !done) { st.mode = "swipe"; clearTimeout(st.hold); (e.currentTarget as Element).setPointerCapture(e.pointerId); }
    }
    if (st.mode === "swipe") setDx(Math.max(-120, Math.min(120, mx)));
    if (st.mode === "drag" && st.w) { const want = toMin(d.start!) + (mx / st.w) * AXIS_MIN; setDrag(clampStart(want, dur, walls).start); }
  };
  const up = async () => {
    const st = g.current; g.current = null; if (!st) return;
    clearTimeout(st.hold);
    if (st.mode === "swipe") {
      const x = dx; setDx(0);
      if (Math.abs(x) > 80) haptic();
      if (x < -80) { const m = await cancelRide(d.date, d.status === "booked"); toast(m, () => restoreRide(d.date)); }
      else if (x > 80 && (d.status === "proposed" || d.status === "ticked")) { await toggleChosen(d.date); toast(d.status === "ticked" ? `${dayLong(d.date)} unchosen.` : `${dayLong(d.date)} chosen.`, () => toggleChosen(d.date)); }
      return;
    }
    if (st.mode === "drag" && drag != null) {
      const was = { start: d.start!, end: d.end! };
      const to = { start: hhmm(drag), end: hhmm(drag + dur) };
      setDrag(null);
      haptic();
      if (to.start !== was.start) { await moveRide(d.date, to); toast(`${dayLong(d.date)} moved to ${to.start}.`, () => moveRide(d.date, was)); }
      return;
    }
    if (st.mode === "none" && Date.now() - st.t < 380) open();
  };
  return (
    <div class={`day-row${d.date === today ? " today" : ""}${past ? " past" : ""}${drag != null ? " dragging" : ""}`}>
      {dx !== 0 && <div class={`swipe-under ${dx < 0 ? "left" : "right"}`} aria-hidden="true"><span class="label">{dx < 0 ? "Call it off" : d.status === "ticked" ? "Unchoose" : "Choose"}</span></div>}
      <div class="day-swipe" style={dx ? `transform:translateX(${dx}px)` : undefined}>
        <div class="day-open" role="button" tabIndex={0} aria-label={`Open ${dayLong(d.date)}`} onKeyDown={(e) => { if (e.key === "Enter") open(); }}
          onPointerDown={down as any} onPointerMove={move as any} onPointerUp={up} onPointerCancel={() => { if (g.current) clearTimeout(g.current.hold); g.current = null; setDx(0); setDrag(null); }}>
          <span class="day-name"><span class="label">{dayShort(d.date)}</span><span class="n">{dayNum(d.date)}</span></span>
          <span class="day-mid" ref={bandRef}>
            <span class={`day-what${isRide ? "" : " quiet"}`}>{isRide ? `${drag != null ? hhmm(drag) : d.start} · ${s.routes.find((r) => r.id === d.route)?.n ?? "City loop"}` : d.status === "cancelled" ? "Off" : d.type === "kept" ? (d.why ?? "Kept").split(",")[0].replace(/^In the diary: /, "") : "Rest"}</span>
            <Band day={day} ticks={false} compact />
          </span>
        </div>
        {isRide && !done && !past && d.status !== "booked" ? (
          <button type="button" class={`tick${d.status === "ticked" ? " on" : ""}`} role="checkbox" aria-checked={d.status === "ticked"} aria-label={`Choose ${dayLong(d.date)}`} onClick={() => toggleChosen(d.date)}>
            {d.status === "ticked" && <Check />}
          </button>
        ) : isRide ? (
          <span class={`tick fixed${done ? " done" : " booked"}`} role="img" aria-label={done ? "Ridden" : "Booked, in the diary"}>{done ? <Check /> : <Booked />}</span>
        ) : <span class="tick none" />}
      </div>
    </div>
  );
}

/** Every other day this week or next a ride could move to, each at its own best start. */
function fitDays(s: State, d: PlanDay, today: string): { date: string; start: string; end: string }[] {
  if (!d.start || !d.end) return [];
  const dur = toMin(d.end) - toMin(d.start);
  const out: { date: string; start: string; end: string }[] = [];
  for (const x of s.plan.flatMap((w) => w.days)) {
    if (x.date === d.date || x.date < today || x.type !== "rest") continue;
    if (x.date > addDays(mondayOf(d.date), 13)) continue;
    const walls = wallsFor(s, x.date);
    if (!validStarts(dur, walls).length) continue;
    const st = clampStart(toMin(d.start), dur, walls).start;
    out.push({ date: x.date, start: hhmm(st), end: hhmm(st + dur) });
  }
  return out.slice(0, 8);
}

function DaySheet({ d, s, today, week, toast, close }: { d: PlanDay; s: State; today: string; week: PlanDay[]; toast: (t: string, u?: () => void) => void; close: () => void }) {
  const [fromDiary, setFromDiary] = useState(true);
  const [busy, setBusy] = useState(false);
  const isRide = d.type === "ride" && d.status !== "cancelled" && d.start && d.end;
  const done = d.status === "done" || s.rides.some((r) => r.d === d.date && r.km >= 15);
  const free = fitDays(s, d, today);
  const statusWord = done ? "Ridden" : d.status === "booked" ? "Booked" : d.status === "ticked" ? "Chosen" : "Proposed";

  if (isRide) return (
    <div class="sheet-stack">
      <RideEditor day={d} toast={toast} label={`${dateLabel(d.date)} · ${statusWord}`} />
      {!done && d.date >= today && (
        <>
          {d.status !== "booked" && (
            <button type="button" class="pill wide" disabled={busy} onClick={async () => { setBusy(true); toast(await bookRides([d.date])); setBusy(false); close(); }}>
              {busy ? "Putting it in the diary" : `Book ${dayLong(d.date)} ${d.start}`}
            </button>
          )}
          {free.length > 0 && (
            <div class="sheet-group">
              <span class="label">Move to another day</span>
              <div class="chips">
                {free.map((x) => (
                  <button type="button" class="chip" onClick={async () => { toast(await moveRide(d.date, { date: x.date, start: x.start, end: x.end })); close(); }}>
                    <span>{x.date > addDays(mondayOf(d.date), 6) ? `${dayLong(x.date)} ${dayNum(x.date)}` : dayLong(x.date)}</span><small>{x.start}</small>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div class="sheet-group">
            {d.status === "booked" && (
              <label class="check-line"><input type="checkbox" checked={fromDiary} onChange={(e) => setFromDiary((e.target as HTMLInputElement).checked)} /> Take it out of the diary too</label>
            )}
            <button type="button" class="text-btn quiet" onClick={async () => { const m = await cancelRide(d.date, fromDiary); toast(m, () => restoreRide(d.date)); close(); }}>Cancel {dayLong(d.date)}</button>
          </div>
        </>
      )}
    </div>
  );
  if (d.status === "cancelled") return (
    <div class="sheet-stack">
      <span class="label">{dateLabel(d.date)} · Off</span>
      <span class="say">{d.start} to {d.end} was called off.</span>
      {d.date >= today && <button type="button" class="pill wide" onClick={async () => { await restoreRide(d.date); toast("Back in the week, proposed."); close(); }}>Put it back</button>}
    </div>
  );
  if (d.type === "kept") return (
    <div class="sheet-stack">
      <span class="label">{dateLabel(d.date)} · Kept</span>
      <span class="say">{(d.why ?? "Kept clear").replace(/^In the diary: /, "")}.</span>
      <span class="soft">Kept evenings stay clear. Nothing to do here.</span>
    </div>
  );
  return (
    <div class="sheet-stack">
      <span class="label">{dateLabel(d.date)} · Rest</span>
      <span class="say">A rest day.</span>
      {d.date >= today && <button type="button" class="pill ghost wide" onClick={async () => { await addRide(d.date); toast(`${dayLong(d.date)} added, proposed.`); }}>Ride {dayLong(d.date)} instead</button>}
    </div>
  );
}

/** In the diary: a calendar with a tick. */
function Booked() {
  return <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6.5h16v13H4zM4 10.5h16M8 4v4M16 4v4" /><path d="M9 15l2 2 4-4" /></svg>;
}

function Check() {
  return <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 6.2l2.2 2.2L9.5 3.6" /></svg>;
}

function rampLine(r: { w: string; km: number | null; rides?: number; note?: string } | undefined, k: number) {
  if (!r) return "";
  const what = r.rides ? `${r.rides} ${r.rides === 1 ? "ride" : "rides"}` : r.km ? `${r.km} km` : "no target";
  return `${k === 0 ? "This week" : `Week of ${shortDate(r.w)}`}: ${what}${r.note ? `. ${r.note}` : ""}.`;
}

function keptWindows(days: PlanDay[]): Kept[] {
  const out: Kept[] = [];
  for (const d of days) {
    if (d.type !== "kept") continue;
    const m = /(\d\d:\d\d) to (\d\d:\d\d)/.exec(d.why ?? "");
    if (m) out.push({ start: toMin(m[1]), end: toMin(m[2]), label: (d.why ?? "").split(",")[0] });
    else if (/isabella/i.test(d.why ?? "")) out.push({ start: 18 * 60, end: 22 * 60, label: "Isabella" });
    else out.push({ start: 17 * 60, end: 22 * 60, label: d.why ?? "Kept" });
  }
  return out;
}

function rideDays(days: { date: string; type: string; status?: string }[]): string {
  const live = days.filter((d) => d.type === "ride" && d.status !== "cancelled");
  if (!live.length) return "No rides this week.";
  const list = live.map((d) => dayLong(d.date));
  return (list.length > 1 ? `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}` : list[0]) + ".";
}
